import { Container } from '@tencent/alloy-renderer';
import {
    InterfaceFactoryOptions,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import {
    getSizeAndFlip,
} from '@editor/src/page/index/components/canvas/element-types/image-element/image-element-container/common';
import {
    AUDIO_DEFAULT_IMG,
    TRANSPARENT_PIXEL_BASE64,
} from '@src/slide/domain/ui-model/element-modeles/property/attachment/constants';
import omit from 'lodash/omit';
import { omitStyle } from '@src/slide/infrastructure/module/independent/assert/get-set';
import { RenderPostionType } from '@src/js/constant';
import { CANVAS_IMAGE_FOREGROUND_ID_PREFIX } from '@src/slide/content-viewer/service/canvas-renderer/core/id-constants';
import {
    checkHasAnimation,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-utils';
import get from 'lodash/get';
import addonDefaultForeground from '@src/img/default-iframe.svg';
import { SourceType } from '@src/slide/domain/ui-model/constant/element-source';
import {
    isAttachmentImageElement,
    isAudioElement,
    isDomGifPictureModel,
    isIframeShapeModel,
    isIframeShapeModelInTextMode,
    isMediaElement,
    isVideoElement,
} from '@src/slide/domain/ui-model/elements/element-judge';
import { IMediaManager } from '@src/slide/application/service/media-manager/media-manager.interface';
import { GraphicsRenderComponent } from '@tencent/alloy-graphics';
import {
    generateGraphicsModel, isSpecialPath,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/graphic-adapter/generate-graphics-model';
import { getPathList } from '@editor/src/page/index/components/canvas/element-types/shape-element/utils';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { enumerateGroup } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/enumerate';
import { reverseComponentFlip } from '../reverse-component-flip';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import expect from '@src/slide/infrastructure/module/independent/assert/expect';
import { ImageTileFlipType } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { EEffectAlgn } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';

export class ForegroundComponent extends Container {
    // 图形构造入参
    public factoryOptions: InterfaceFactoryOptions<IPictureModel | IShapeModel>;
    // 图片前景graphics
    public foregroundGraphics: GraphicsRenderComponent;
    // 正在请求图片附件封面图
    private isFetchingPoster = false;

    public constructor(factoryOptions: InterfaceFactoryOptions<IPictureModel | IShapeModel>) {
        super();
        this.factoryOptions = factoryOptions;
        const { model } = this.factoryOptions;
        const { mediaManager } = this.factoryOptions;
        this.id = `${CANVAS_IMAGE_FOREGROUND_ID_PREFIX}-${model.key}`;

        // iframe 情况下拉取首帧图
        if (isIframeShapeModel(model) || isIframeShapeModelInTextMode(model)) {
            mediaManager?.fetchIframePoster(model);
        }
    }

    /**
     * 渲染前景图片
     */
    public renderComponent() {
        const { model, mediaManager } = this.factoryOptions;
        // iframe 情况下拉取首帧图
        if (isIframeShapeModel(model) || isIframeShapeModelInTextMode(model)) {
            mediaManager?.fetchIframePoster(model);
        }
        const style = this.generateForegroundStyle();
        const graphicsModel = this.generateGraphicsModel(style);
        if (!this.foregroundGraphics) {
            this.foregroundGraphics = new GraphicsRenderComponent(graphicsModel);
            this.addChild(this.foregroundGraphics);
        } else {
            this.foregroundGraphics.updateModel(graphicsModel);
        }

        this.dealFlip();
        this.dealVisibleStatus();
    }

    /**
     * 处理编辑区图形的显示状态
     * @param forceVisible 强制显示或隐藏
     */
    // eslint-disable-next-line complexity
    public dealVisibleStatus = (forceVisible?: boolean): void => {
        if (typeof forceVisible === 'boolean') {
            if (this.foregroundGraphics) {
                this.foregroundGraphics.visible = forceVisible;
            }

            return;
        }

        const { isCroping, model, region } = this.factoryOptions;
        const { mediaManager } = this.factoryOptions;
        if (!mediaManager) return;
        if (region === RenderPostionType.CANVAS) {
            this.foregroundGraphics.visible = this.shouldCanvasForegroundVisible(
                isCroping ?? false,
                model,
                mediaManager,
            );
        } else if (region === RenderPostionType.PLAYER) {
            if (isDomGifPictureModel(model) && !this.isMasterLayoutModel(model)) {
                const hasAnimationOrGroupHasAnimation = this.checkHasAnimation(model, model.slideRef ?? undefined);
                this.foregroundGraphics.visible = hasAnimationOrGroupHasAnimation
                    && mediaManager?.shouldPlayerRenderGifPoster(model.key);
                this.factoryOptions.getApplication?.()?.render();
            }
        }
    };

    /**
     * 音视频附件 dom 本身不翻转
     * 所以音视频封面图也不翻转
     * @returns
     */
    protected dealFlip() {
        if (!isMediaElement(this.factoryOptions.model)) {
            return;
        }
        this.foregroundGraphics = reverseComponentFlip(this.foregroundGraphics, this.factoryOptions);
    }

    /**
     * 生成图片前景graphics所需的style
     */
    protected generateForegroundStyle = () => {
        const modelProps = (this.factoryOptions.model as IPictureModel).props;
        const { src, backupSrc, imageType } = modelProps;
        const rect = this.factoryOptions.frame!;
        const { size } = getSizeAndFlip(rect, modelProps);
        const srcRect = rect?.srcRect ? rect.srcRect : modelProps.style.srcRect;
        let stretch;
        let { tile } = modelProps.style;
        const { model } = this.factoryOptions;
        if  (isAttachmentImageElement(model) || isIframeShapeModel(model)) {
            // 图片附件的裁剪属性是用于导出时保证不变形的，渲染时忽略裁剪属性，动态根据图片比例计算 frame 来保证不变形
            stretch = undefined;
        } else if (srcRect && (isVideoElement(model) || modelProps.style.stretch)) {
            // 图片设置的充满
            stretch = { srcRect };
        } else if (!tile) {
            // 图片充满和平铺都没有数据，则用默认平铺兜底（对齐office）
            tile = {
                tx: 0, ty: 0, sx: 100, sy: 100, flip: ImageTileFlipType.NONE, algn: EEffectAlgn.TL,
            };
        }

        const image = {
            src: this.generateSource(src),
            backupSrc,
            imageType,
            alpha: modelProps.style.alpha,
            isForeground: true,
            nonStandardParams: this.getNonStandardParams(this.factoryOptions.model),
            stretch,
            tile,
        };

        let offsetFrame;
        const { bgShape } = modelProps.style;
        if (isSpecialPath(bgShape)) {
            // 特殊图形有部分区域在选中框外，导致图片裁剪后选中框外不显示图片，使用 offsetFrame 根据形状区域扩展图片
            const wrapRect = this.factoryOptions.model.pathParser.getShapeWrapRect();
            const frameOnSlide = model.getFrame?.().frameOnSlide();
            offsetFrame = {
                offsetX: wrapRect.left,
                offsetY: wrapRect.top,
                offsetWidth: wrapRect.width,
                offsetHeight: wrapRect.height,
                offsetFlipH: !!(frameOnSlide.flipH),
                offsetFlipV: !!(frameOnSlide.flipV),
                offsetRotate: 0,
            };
        }
        return {
            ...omit(omitStyle(modelProps.style), 'width', 'height', 'border'),
            ...size,
            fillBackground: { image },
            controlValues: this.factoryOptions.model.operatingGraphicInfo?.controlValues
                ?? modelProps.style.controlValues,
            pathList: getPathList(this.factoryOptions.model),
            offsetFrame,  // 前景图无需偏移
        };
    };

    private checkHasAnimation(model: IBaseModel, slideModel: ISlideModel | undefined): boolean {
        let hasAnimation = checkHasAnimation(model.id ?? '', slideModel);
        enumerateGroup(model, (group) => {
            hasAnimation = hasAnimation || checkHasAnimation(group.id ?? '', slideModel);
            return hasAnimation;
        });
        return hasAnimation;
    }

    private shouldCanvasForegroundVisible(isCroping: boolean, model, mediaManager?: IMediaManager): boolean {
        // 裁剪过程中，渲染层的图片前景延迟，只显示背景
        return !isCroping
        // gif 非选中情况下隐藏 canvas 图
        && (!isDomGifPictureModel(model)
            // 母版的 gif 元素不隐藏
            || this.isMasterLayoutModel(model)
            || !!mediaManager?.shouldRenderGifPoster(model.key));
    }

    /**
     * 根据style生成graphics构造所需的model
     * @param style
     */
    private generateGraphicsModel = (style) => {
        const elementModel = this.factoryOptions.model;
        const frameOnSlide = elementModel.getFrame?.().frameOnSlide();
        const realRotateAndFlip = {
            rotate: frameOnSlide.rotate,
            flipH: frameOnSlide.flipH,
            flipV: frameOnSlide.flipV,
        };
        return generateGraphicsModel({
            style,
            factoryOptions: this.factoryOptions,
            realRotateAndFlip,
        });
    };

    /**
     * 根据场景返回图片前景的 src
     * @param defaultSource sdk 返回的默认图片
     */
    // eslint-disable-next-line complexity
    private generateSource(defaultSource: string) {
        const { model } = this.factoryOptions;

        // 音频占位图标
        if (isAudioElement(model)) {
            return AUDIO_DEFAULT_IMG;
        }

        // 图片附件动态从 dc 中去拿图片地址
        if (isAttachmentImageElement(model)) {
            // 目前只有左侧缩略图的图片附件需要 slide 渲染，其他场景都是用 dc 组件渲染的，所以直接返回占位图即可
            if (!this.factoryOptions.fromScale) {
                return TRANSPARENT_PIXEL_BASE64;
            }
            if (this.isFetchingPoster) {
                // 已经有未完成的封面图请求，避免重复发送请求
                return TRANSPARENT_PIXEL_BASE64;
            }
            const urlOrPromise = this.factoryOptions.mediaManager?.fetchImageAttachmentPoster(model);
            if (!expect(urlOrPromise).isPromise()) {
                return urlOrPromise || TRANSPARENT_PIXEL_BASE64;
            }
            // 返回 promise，说明当前还没获取到图片地址，此时直接返回默认图片，获取完之后再重新渲染
            this.isFetchingPoster = true;
            (urlOrPromise as Promise<string | null>)?.then((url) => {
                this.isFetchingPoster = false;
                // 避免接口请求出错后重复渲染重复触发请求导致死循环
                url && this.renderComponent();
            });
            return TRANSPARENT_PIXEL_BASE64;
        }

        // iframe 截图
        if (isIframeShapeModel(model)) {
            const iframeScreenShot = this.factoryOptions.mediaManager?.iframePosterCaches.get(model.key);
            // 使用 iframePosterCaches.get 方法获取截图，请求抛错会返回一个结构体，所以需要做一次判断来兜底渲染
            return (iframeScreenShot && typeof (iframeScreenShot) === 'string') ? iframeScreenShot : addonDefaultForeground;
        }

        return defaultSource;
    }

    private getNonStandardParams(model) {
        return {
            chartRelation: get(model, 'sdkData.image.chartRelation'),
            chartStaticFolder: get(model, 'sdkData.image.chartStaticFolder'),
        };
    }

    private isMasterLayoutModel(model) {
        return model.sourceType === SourceType.LAYOUT || model.sourceType === SourceType.MASTER;
    }
}
